import styled from "@xstyled/styled-components";
import { useMemo } from "react";

import { JobStatuses } from "./PaginatedTable";

import { palette } from "@otta/design-tokens";
import { Checkbox } from "@toolbox/components/Input/Checkbox";
import { JobsList, JobWorkflowStatus } from "@toolbox/schema";

const HeaderContainer = styled.thead``;

const Row = styled.tr``;

const HeaderCell = styled.th`
  padding: xs;
  font-weight: 600;
  text-align: left;
`;

const SelectionCell = styled(HeaderCell)`
  border-left: 1px solid ${palette.grayscale.shade400};
`;

interface HeaderRowProps {
  jobs: JobsList.Jobs[];
  unsavedChanges: JobStatuses;
  handleToggle(payload: {
    ids: string[];
    workflowStatus: JobWorkflowStatus;
    disapprovalReason?: string;
  }): void;
}

export const HeaderRow = ({
  jobs,
  unsavedChanges,
  handleToggle,
}: HeaderRowProps) => {
  const allDisapprovedLocation = useMemo(
    () =>
      jobs.every(
        j =>
          unsavedChanges.get(j.id)?.workflowStatus ===
            JobWorkflowStatus.Disapproved &&
          unsavedChanges.get(j.id)?.disapprovalReason === "LOCATION"
      ),
    [jobs, unsavedChanges]
  );

  const allDisapprovedFunction = useMemo(
    () =>
      jobs.every(
        j =>
          unsavedChanges.get(j.id)?.workflowStatus ===
            JobWorkflowStatus.Disapproved &&
          unsavedChanges.get(j.id)?.disapprovalReason === "UNSUPPORTED_FUNCTION"
      ),
    [jobs, unsavedChanges]
  );

  const allDisapprovedOther = useMemo(
    () =>
      jobs.every(
        j =>
          unsavedChanges.get(j.id)?.workflowStatus ===
            JobWorkflowStatus.Disapproved &&
          unsavedChanges.get(j.id)?.disapprovalReason === undefined
      ),
    [jobs, unsavedChanges]
  );

  const allApproved = useMemo(
    () =>
      jobs.every(
        j =>
          unsavedChanges.get(j.id)?.workflowStatus ===
          JobWorkflowStatus.QueuedExternal
      ),
    [jobs, unsavedChanges]
  );

  const toggleAll = (
    workflowStatus: JobWorkflowStatus,
    disapprovalReason?: string
  ) => {
    handleToggle({
      ids: jobs.map(job => job.id),
      workflowStatus,
      disapprovalReason,
    });
  };

  return (
    <HeaderContainer>
      <Row>
        <HeaderCell>Company Name</HeaderCell>
        <HeaderCell>Job Title</HeaderCell>
        <HeaderCell>Sub Function</HeaderCell>
        <HeaderCell>Original URL</HeaderCell>
        <HeaderCell>Scraped Location</HeaderCell>
        <HeaderCell>Location Preferences</HeaderCell>
        <SelectionCell>
          Disapprove location (1)
          <Checkbox
            data-testid="disapprove-all-location"
            checked={allDisapprovedLocation}
            onChange={() => {
              toggleAll(JobWorkflowStatus.Disapproved, "LOCATION");
            }}
          />
        </SelectionCell>
        <SelectionCell>
          Disapprove function (2)
          <Checkbox
            data-testid="disapprove-all-function"
            checked={allDisapprovedFunction}
            onChange={() => {
              toggleAll(JobWorkflowStatus.Disapproved, "UNSUPPORTED_FUNCTION");
            }}
          />
        </SelectionCell>
        <SelectionCell>
          Disapprove other (3)
          <Checkbox
            data-testid="disapprove-all-other"
            checked={allDisapprovedOther}
            onChange={() => {
              toggleAll(JobWorkflowStatus.Disapproved);
            }}
          />
        </SelectionCell>
        <SelectionCell>
          Queue
          <Checkbox
            data-testid="queue-all"
            checked={allApproved}
            onChange={() => {
              toggleAll(JobWorkflowStatus.QueuedExternal);
            }}
          />
        </SelectionCell>
      </Row>
    </HeaderContainer>
  );
};
